import { getApiClient } from '../api-client-factory';
import { API_ENDPOINTS } from '../api-endpoints';
import type { ApiResponse } from '../api-types';
import type {
  UserCareerProfile,
  PublicProfileResult,
  ProfileCompletionScore,
  UpsertCareerProfileRequest,
  CareerExperience,
  CreateExperienceRequest,
  UpdateExperienceRequest,
  CareerEducation,
  CreateEducationRequest,
  UpdateEducationRequest,
  CareerProject,
  CreateProjectRequest,
  UpdateProjectRequest,
  CareerCertification,
  CreateCertificationRequest,
  UpdateCertificationRequest,
  CareerLanguage,
  CreateLanguageRequest,
  UpdateLanguageRequest,
  CareerSkill,
  CreateSkillRequest,
  UpdateSkillRequest,
} from '@welynk/types';

export class CareerProfileApiService {
  // ── Profile ──────────────────────────────────────────────────────────────────

  static async getProfile(): Promise<UserCareerProfile | null> {
    const client = getApiClient();
    const res = await client.get<ApiResponse<UserCareerProfile | null>>(API_ENDPOINTS.PROFILE.GET);
    return res.data.data;
  }

  static async getPublicProfile(profileId: string): Promise<PublicProfileResult | null> {
    const client = getApiClient();
    try {
      const res = await client.get<ApiResponse<PublicProfileResult>>(
        API_ENDPOINTS.PUBLIC_PROFILE(profileId)
      );
      return res.data.data ?? null;
    } catch (err: any) {
      if (err?.response?.status === 404) return null;
      throw err;
    }
  }

  static async uploadAvatar(file: File): Promise<UserCareerProfile> {
    const client = getApiClient();
    const formData = new FormData();
    formData.append('file', file);
    const res = await client.post<ApiResponse<UserCareerProfile>>(
      API_ENDPOINTS.PROFILE.AVATAR_UPLOAD,
      formData
    );
    return res.data.data;
  }

  /** Set the public link handle (/u/<handle>); null clears it. */
  static async setHandle(handle: string | null): Promise<UserCareerProfile> {
    const client = getApiClient();
    const res = await client.put<ApiResponse<UserCareerProfile>>(API_ENDPOINTS.PROFILE.HANDLE, {
      handle,
    });
    return res.data.data;
  }

  static async upsertProfile(data: UpsertCareerProfileRequest): Promise<UserCareerProfile> {
    const client = getApiClient();
    const res = await client.patch<ApiResponse<UserCareerProfile>>(
      API_ENDPOINTS.PROFILE.UPSERT,
      data
    );
    return res.data.data;
  }

  static async saveWizardStep(
    step: number,
    data: UpsertCareerProfileRequest
  ): Promise<UserCareerProfile> {
    const client = getApiClient();
    const res = await client.post<ApiResponse<UserCareerProfile>>(
      API_ENDPOINTS.PROFILE.WIZARD_STEP,
      { ...data, wizardStep: step }
    );
    return res.data.data;
  }

  static async completeWizard(): Promise<UserCareerProfile> {
    const client = getApiClient();
    const res = await client.post<ApiResponse<UserCareerProfile>>(
      API_ENDPOINTS.PROFILE.WIZARD_COMPLETE,
      {}
    );
    return res.data.data;
  }

  static async getCompletion(): Promise<ProfileCompletionScore | null> {
    const client = getApiClient();
    const res = await client.get<ApiResponse<ProfileCompletionScore | null>>(
      API_ENDPOINTS.PROFILE.COMPLETION
    );
    return res.data.data;
  }

  // ── Experiences ──────────────────────────────────────────────────────────────

  static async addExperience(data: CreateExperienceRequest): Promise<CareerExperience> {
    const client = getApiClient();
    const res = await client.post<ApiResponse<CareerExperience>>(
      API_ENDPOINTS.PROFILE.ADD_EXPERIENCE,
      data
    );
    return res.data.data;
  }

  static async updateExperience(
    id: string,
    data: UpdateExperienceRequest
  ): Promise<CareerExperience> {
    const client = getApiClient();
    const res = await client.patch<ApiResponse<CareerExperience>>(
      API_ENDPOINTS.PROFILE.UPDATE_EXPERIENCE(id),
      data
    );
    return res.data.data;
  }

  static async deleteExperience(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.PROFILE.DELETE_EXPERIENCE(id));
  }

  // ── Education ────────────────────────────────────────────────────────────────

  static async addEducation(data: CreateEducationRequest): Promise<CareerEducation> {
    const client = getApiClient();
    const res = await client.post<ApiResponse<CareerEducation>>(
      API_ENDPOINTS.PROFILE.ADD_EDUCATION,
      data
    );
    return res.data.data;
  }

  static async updateEducation(id: string, data: UpdateEducationRequest): Promise<CareerEducation> {
    const client = getApiClient();
    const res = await client.patch<ApiResponse<CareerEducation>>(
      API_ENDPOINTS.PROFILE.UPDATE_EDUCATION(id),
      data
    );
    return res.data.data;
  }

  static async deleteEducation(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.PROFILE.DELETE_EDUCATION(id));
  }

  // ── Projects ─────────────────────────────────────────────────────────────────

  static async addProject(data: CreateProjectRequest): Promise<CareerProject> {
    const client = getApiClient();
    const res = await client.post<ApiResponse<CareerProject>>(
      API_ENDPOINTS.PROFILE.ADD_PROJECT,
      data
    );
    return res.data.data;
  }

  static async updateProject(id: string, data: UpdateProjectRequest): Promise<CareerProject> {
    const client = getApiClient();
    const res = await client.patch<ApiResponse<CareerProject>>(
      API_ENDPOINTS.PROFILE.UPDATE_PROJECT(id),
      data
    );
    return res.data.data;
  }

  static async deleteProject(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.PROFILE.DELETE_PROJECT(id));
  }

  // ── Certifications ───────────────────────────────────────────────────────────

  static async addCertification(data: CreateCertificationRequest): Promise<CareerCertification> {
    const client = getApiClient();
    const res = await client.post<ApiResponse<CareerCertification>>(
      API_ENDPOINTS.PROFILE.ADD_CERTIFICATION,
      data
    );
    return res.data.data;
  }

  static async updateCertification(
    id: string,
    data: UpdateCertificationRequest
  ): Promise<CareerCertification> {
    const client = getApiClient();
    const res = await client.patch<ApiResponse<CareerCertification>>(
      API_ENDPOINTS.PROFILE.UPDATE_CERTIFICATION(id),
      data
    );
    return res.data.data;
  }

  static async deleteCertification(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.PROFILE.DELETE_CERTIFICATION(id));
  }

  // ── Languages ────────────────────────────────────────────────────────────────

  static async addLanguage(data: CreateLanguageRequest): Promise<CareerLanguage> {
    const client = getApiClient();
    const res = await client.post<ApiResponse<CareerLanguage>>(
      API_ENDPOINTS.PROFILE.ADD_LANGUAGE,
      data
    );
    return res.data.data;
  }

  static async updateLanguage(id: string, data: UpdateLanguageRequest): Promise<CareerLanguage> {
    const client = getApiClient();
    const res = await client.patch<ApiResponse<CareerLanguage>>(
      API_ENDPOINTS.PROFILE.UPDATE_LANGUAGE(id),
      data
    );
    return res.data.data;
  }

  static async deleteLanguage(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.PROFILE.DELETE_LANGUAGE(id));
  }

  // ── Skills ───────────────────────────────────────────────────────────────────

  static async addSkill(data: CreateSkillRequest): Promise<CareerSkill> {
    const client = getApiClient();
    const res = await client.post<ApiResponse<CareerSkill>>(API_ENDPOINTS.PROFILE.ADD_SKILL, data);
    return res.data.data;
  }

  static async replaceSkills(skills: CreateSkillRequest[]): Promise<CareerSkill[]> {
    const client = getApiClient();
    const res = await client.put<ApiResponse<CareerSkill[]>>(API_ENDPOINTS.PROFILE.REPLACE_SKILLS, {
      skills,
    });
    return res.data.data;
  }

  static async updateSkill(id: string, data: UpdateSkillRequest): Promise<CareerSkill> {
    const client = getApiClient();
    const res = await client.patch<ApiResponse<CareerSkill>>(
      API_ENDPOINTS.PROFILE.UPDATE_SKILL(id),
      data
    );
    return res.data.data;
  }

  static async deleteSkill(id: string): Promise<void> {
    const client = getApiClient();
    await client.delete(API_ENDPOINTS.PROFILE.DELETE_SKILL(id));
  }
}
